import React from 'react';
import { ArrowLeft, ArrowUpRight, Clock } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import FooterCTA from '../components/site/FooterCTA';
import Footer from '../components/site/Footer';
import { blogPosts, getBlogPostById } from '../data/mock';

const RelatedCard = ({ post }) => (
  <Link
    to={`/blog/${post.id}`}
    className="group block rounded-3xl overflow-hidden border border-[#12367F]/10 bg-white hover:-translate-y-1 transition-transform"
  >
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={post.image}
        alt={post.title}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
    <div className="p-6">
      <div className="flex items-center gap-3 flex-wrap text-xs uppercase tracking-[0.22em] text-[#12367F]/65">
        <span>{post.category}</span>
        <span>{post.readTime}</span>
      </div>
      <h3 className="mt-4 font-display uppercase text-2xl leading-[0.95] text-[#12367F]">{post.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-[#12367F]/75">{post.excerpt}</p>
      <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#12367F]">
        Read story <ArrowUpRight className="h-4 w-4" />
      </div>
    </div>
  </Link>
);

const BlogPost = () => {
  const { id } = useParams();
  const post = getBlogPostById(id);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = blogPosts.filter((entry) => entry.id !== post.id).slice(0, 3);

  return (
    <main className="bg-white text-[#12367F]">
      <Navbar />

      <section className="pt-28 md:pt-36 pb-10 md:pb-14">
        <div className="container-x px-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-[#12367F]/70 hover:text-[#12367F]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to blog
          </Link>

          <div className="mt-8 max-w-5xl">
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="inline-block rounded-md px-4 py-1.5 font-display text-sm uppercase tracking-wider"
                style={{ background: '#2EF5FF', color: '#12367F' }}
              >
                {post.category}
              </span>
              <span className="inline-flex items-center gap-2 rounded-md bg-[#12367F]/8 px-4 py-1.5 font-display text-sm uppercase tracking-wider text-[#12367F]">
                <Clock className="h-4 w-4" />
                {post.readTime}
              </span>
            </div>

            <h1
              className="mt-8 font-display uppercase text-balance text-[#12367F]"
              style={{ fontSize: 'clamp(2.6rem, 7vw, 6.5rem)', lineHeight: 0.92 }}
            >
              {post.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm uppercase tracking-[0.22em] text-[#12367F]/60">
              <span>{post.author}</span>
              <span>{post.publishedAt}</span>
            </div>

            <p className="mt-8 max-w-3xl text-lg leading-relaxed text-[#12367F]/78">
              {post.intro}
            </p>
          </div>
        </div>
      </section>

      <section className="pb-12 md:pb-16">
        <div className="container-x px-6">
          <div className="relative overflow-hidden rounded-[2rem] bg-[#12367F]">
            <img
              src={post.image}
              alt={post.title}
              className="h-[300px] md:h-[520px] w-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12367F]/85 via-[#12367F]/30 to-transparent" />
          </div>
        </div>
      </section>

      <article className="pb-20 md:pb-28">
        <div className="container-x px-6">
          <div className="mx-auto max-w-3xl space-y-14">
            {post.story.map((section) => (
              <section key={section.heading}>
                <h2 className="font-display uppercase text-3xl md:text-4xl leading-[0.95] text-[#12367F]">
                  {section.heading}
                </h2>
                <div className="mt-6 space-y-5 text-base md:text-lg leading-relaxed text-[#12367F]/82">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </article>

      <section className="pb-20 md:pb-28">
        <div className="container-x px-6">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#12367F]/60">Keep Reading</p>
              <h2 className="mt-3 font-display uppercase text-4xl md:text-5xl leading-none text-[#12367F]">
                More Stories
              </h2>
            </div>
            <Link to="/blog" className="hidden md:inline-flex btn-base btn-outline-dark">
              View all posts <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPosts.map((entry) => (
              <RelatedCard key={entry.id} post={entry} />
            ))}
          </div>
        </div>
      </section>

      <FooterCTA />
      <Footer />
    </main>
  );
};

export default BlogPost;
